import { Injectable } from "@angular/core"
import { Misc } from "@common-lib/modules/misc"
import { ParentClass } from "../../parent"
import { CurrentSongAudioBuffer, PlayerState } from "./player.store"

import { MAX_LOADING } from "@common-lib/constants/constants"

let AudioContext = window["AudioContext"] || window["webkitAudioContext"]

export enum PlaybackActionType {
    PLAY = "play",
    PAUSE = "pause",
    SEEK = "seek",
}

export function shouldAdvanceFromCurrentTrack(
    currentTime: number,
    song?: { duration: number; loading: number } | null
): boolean {
    if (song == null) {
        return false
    }

    if (song.loading < MAX_LOADING || !(song.duration > 0)) {
        return false
    }

    return currentTime + 0.01 >= song.duration
}

@Injectable()
export class PlaybackEngine extends ParentClass {
    private readonly mimeType = "audio/mpeg"

    // MediaSource API Playback
    public audioPlayer = new Audio()
    public mediaSource: MediaSource | null = this.createMediaSource()
    private sourceBuffer: SourceBuffer
    public mediaSourceSupport = true
    private appendedEncodedBytes = 0
    private encodedSnapshot: Uint8Array | null = null
    private hotLoadQueue: Promise<void> = Promise.resolve()
    private audioObjectURL: string | null = null
    private pendingMediaSourceOpen: {
        source: MediaSource
        handler: EventListener
        resolve: (source: MediaSource | null) => void
    } | null = null
    private mediaSourceInitialization: Promise<MediaSource | null> | null = null

    // Web Audio API Playback
    private masterBus: AudioContext = new AudioContext()
    private audioContextGain: GainNode = this.masterBus.createGain()
    private soundSource: AudioBufferSourceNode
    private currentDecodedBuffer: AudioBuffer | null = null
    private webAudioSourceStarted = false
    private webAudioOriginContextTime = 0
    private webAudioOriginSeek = 0
    private webAudioPausedPosition: number | null = 0

    private playbackState: {
        action: PlaybackActionType
        seekTime: number
        songID: string | null
    } | null = null
    private playbackActionVersion = 0

    private preloadedBuffers: CurrentSongAudioBuffer[] = []
    private unsortedBuffers: {
        buffer: CurrentSongAudioBuffer
        index: number
    }[] = []

    private lastPreloadedBufferIndex: number = -1

    private animationFrameID: number = null
    private destroyed = false
    private endedSongID: string | null = null

    constructor(
        private getCurrentState: () => PlayerState,
        private setTime: (time: number) => void,
        private onTrackEnded: () => Promise<void>,
        private setDecodedBuffer: (
            preLoadedBuffers: CurrentSongAudioBuffer[]
        ) => void,
        private onPlaybackError: (error: unknown) => void = () => {}
    ) {
        super()

        this.mediaSourceSupport = this.supportsMediaSourceType()

        this.animationFrameID = requestAnimationFrame(
            this.animationFrameLoop.bind(this)
        )
    }

    /**
     * Destroys the playback engine and cleans up any resources.
     * This prevents memory leaks and ensures that the engine is properly disposed of when no longer needed.
     */
    public async destroy() {
        if (this.destroyed) {
            return
        }

        this.destroyed = true
        await this.pause()
        this.stopAnimationFrameLoop()
        this.stopWebAudioSource()

        try {
            this.audioContextGain?.disconnect()
        } catch {}

        if (this.masterBus.state !== "closed") {
            try {
                await this.masterBus.close()
            } catch {}
        }

        this.audioPlayer.pause()
        this.audioPlayer.removeAttribute("src")
        this.audioPlayer.load()

        if (this.audioObjectURL !== null) {
            URL.revokeObjectURL(this.audioObjectURL)
            this.audioObjectURL = null
        }

        if (this.pendingMediaSourceOpen !== null) {
            const pending = this.pendingMediaSourceOpen
            pending.source.removeEventListener("sourceopen", pending.handler)
            this.pendingMediaSourceOpen = null
            pending.resolve(null)
        }

        this.mediaSource = null
        this.sourceBuffer = null
        this.preloadedBuffers = []
        this.unsortedBuffers = []
        this.encodedSnapshot = null
    }

    /**
     * Registers a playback action and seek time.
     *
     * @param action - The playback action type.
     * @param seekTime - The seek time in milliseconds.
     */
    public registerAction(action: PlaybackActionType | null, seekTime: number) {
        this.playbackActionVersion += 1

        if (action === null) {
            this.playbackState = null
            return
        }

        this.playbackState = {
            action,
            seekTime,
            songID:
                this.getCurrentState().songQueue.currentSong?.song.id ?? null,
        }
    }

    public setVolume(volume: number) {
        if (this.mediaSourceSupport) {
            this.audioPlayer.volume = volume
        } else if (this.audioContextGain) {
            this.audioContextGain.gain.setValueAtTime(volume, 0)
        }
    }
    private async addAsPreloadedBuffer(
        buffer: CurrentSongAudioBuffer,
        index: number
    ) {
        // Claim the index before awaiting an append so duplicate socket
        // deliveries cannot enter this method concurrently.
        this.lastPreloadedBufferIndex = index
        this.preloadedBuffers.push(buffer)
        if (buffer.buffer instanceof AudioBuffer) {
            this.currentDecodedBuffer = buffer.buffer
        }
        this.setDecodedBuffer(this.preloadedBuffers)

        if (this.mediaSourceSupport) {
            await this.appendToSourceBuffer(buffer)
            if (buffer.buffer instanceof ArrayBuffer) {
                this.appendedEncodedBytes = Math.max(
                    this.appendedEncodedBytes,
                    buffer.buffer.byteLength
                )
            }

            const bufferedEnd = this.getBufferedEnd()
            if (bufferedEnd > 0) {
                buffer.end = bufferedEnd
            }
        }
    }

    public hotLoadBuffer(
        buffer: CurrentSongAudioBuffer,
        index: number
    ): Promise<void> {
        const operation = this.hotLoadQueue.then(() =>
            this.performHotLoadBuffer(buffer, index)
        )
        this.hotLoadQueue = operation.catch(() => undefined)
        return operation
    }

    private async performHotLoadBuffer(
        buffer: CurrentSongAudioBuffer,
        index: number
    ): Promise<void> {
        if (buffer.buffer instanceof AudioBuffer) {
            await this.hotLoadDecodedBuffer(buffer)
            return
        }

        const encoded = buffer.buffer as ArrayBuffer

        // The Web Audio fallback cannot append encoded MP3 continuations. Keep
        // the last decodable snapshot playing until a cumulative snapshot can
        // be decoded by PlayerService.
        if (!this.mediaSourceSupport) {
            return
        }

        const incomingSnapshot = new Uint8Array(encoded)
        if (
            this.encodedSnapshot !== null &&
            !this.isCumulativeSnapshot(this.encodedSnapshot, incomingSnapshot)
        ) {
            return
        }

        this.preloadedBuffers = [buffer]
        this.unsortedBuffers = []
        this.lastPreloadedBufferIndex = index

        if (!this.mediaSource || !this.sourceBuffer) {
            await this.initializeMediaSource()
            this.appendedEncodedBytes = 0
        }

        if (encoded.byteLength > this.appendedEncodedBytes) {
            const nextChunk = encoded.slice(this.appendedEncodedBytes)
            await this.waitForSourceBufferUpdate()
            this.sourceBuffer.appendBuffer(nextChunk)
            this.appendedEncodedBytes = encoded.byteLength
            await this.waitForSourceBufferUpdate()
        }

        this.encodedSnapshot = incomingSnapshot.slice()

        const bufferedEnd = this.getBufferedEnd()
        if (bufferedEnd > 0) {
            this.preloadedBuffers[0].end = bufferedEnd
        }
        this.lastPreloadedBufferIndex = 0
        this.setDecodedBuffer(this.preloadedBuffers)
    }

    private isCumulativeSnapshot(
        previous: Uint8Array,
        incoming: Uint8Array
    ): boolean {
        if (incoming.byteLength < previous.byteLength) {
            return false
        }

        for (let index = 0; index < previous.byteLength; index++) {
            if (previous[index] !== incoming[index]) {
                return false
            }
        }

        return true
    }

    private async hotLoadDecodedBuffer(
        buffer: CurrentSongAudioBuffer
    ): Promise<void> {
        const incoming = buffer.buffer as AudioBuffer
        const previous = this.currentDecodedBuffer
        const decoded =
            previous !== null && previous.duration > incoming.duration
                ? previous
                : incoming
        const playbackTime = this.getPlaybackTime()
        const grew =
            previous === null || decoded.duration > previous.duration + 0.001

        this.currentDecodedBuffer = decoded
        this.preloadedBuffers = [
            {
                start: 0,
                end: Math.max(buffer.end, decoded.duration),
                buffer: decoded,
            },
        ]
        this.unsortedBuffers = []
        this.lastPreloadedBufferIndex = 0
        this.setDecodedBuffer(this.preloadedBuffers)

        if (
            this.getCurrentState().status === "playing" &&
            (!this.webAudioSourceStarted || grew) &&
            playbackTime < decoded.duration
        ) {
            await this.masterBus.resume()
            if (this.getCurrentState().status === "playing") {
                this.playAudioBuffer(decoded, playbackTime)
            } else if (this.masterBus.state === "running") {
                await this.masterBus.suspend()
            }
        }
    }

    public async addBuffer(buffer: CurrentSongAudioBuffer, index: number) {
        if (
            index <= this.lastPreloadedBufferIndex ||
            this.unsortedBuffers.some(queued => queued.index === index)
        ) {
            return
        }

        if (index === this.lastPreloadedBufferIndex + 1) {
            await this.addAsPreloadedBuffer(buffer, index)
        } else {
            this.unsortedBuffers.push({ buffer, index })
        }

        await this.addUnsortedBuffers()
    }

    public async addUnsortedBuffers() {
        this.unsortedBuffers.sort((a, b) => a.index - b.index)

        while (
            this.unsortedBuffers.length > 0 &&
            this.unsortedBuffers[0].index <= this.lastPreloadedBufferIndex
        ) {
            this.unsortedBuffers.shift()
        }

        while (this.unsortedBuffers.length > 0) {
            if (
                this.unsortedBuffers[0].index ===
                this.lastPreloadedBufferIndex + 1
            ) {
                const { buffer, index } = this.unsortedBuffers.shift()

                await this.addAsPreloadedBuffer(buffer, index)
            } else {
                break
            }
        }
    }

    private async initializeMediaSource(): Promise<MediaSource | null> {
        if (this.destroyed) {
            return null
        }

        if (this.mediaSourceInitialization !== null) {
            return this.mediaSourceInitialization
        }

        this.mediaSourceInitialization = new Promise(resolve => {
            const source = new MediaSource()
            this.mediaSource = source
            const finish = (value: MediaSource | null) => {
                this.mediaSourceInitialization = null
                resolve(value)
            }
            const sourceOpenHandler = () => {
                if (this.pendingMediaSourceOpen?.source === source) {
                    this.pendingMediaSourceOpen = null
                }

                source.removeEventListener("sourceopen", sourceOpenHandler)
                if (
                    this.destroyed ||
                    this.mediaSource !== source ||
                    source.readyState !== "open"
                ) {
                    finish(null)
                    return
                }

                this.sourceBuffer = source.addSourceBuffer(this.mimeType)
                finish(source)
            }
            source.addEventListener("sourceopen", sourceOpenHandler)
            this.pendingMediaSourceOpen = {
                source,
                handler: sourceOpenHandler,
                resolve: finish,
            }

            // Set the audio player source to the MediaSource object.
            if (this.audioObjectURL !== null) {
                URL.revokeObjectURL(this.audioObjectURL)
            }
            this.audioObjectURL = URL.createObjectURL(source)
            this.audioPlayer.src = this.audioObjectURL
        })

        return this.mediaSourceInitialization
    }

    private stopAnimationFrameLoop() {
        if (this.animationFrameID !== null) {
            cancelAnimationFrame(this.animationFrameID)
            this.animationFrameID = null
        }
    }

    private async animationFrameLoop() {
        if (this.playbackState !== null) {
            const pendingAction = this.playbackState
            const pendingVersion = this.playbackActionVersion
            await this.handlePlaybackActions(
                pendingAction.action,
                pendingAction.seekTime,
                pendingAction.songID,
                pendingVersion
            )
        }

        const currentTime = this.getPlaybackTime()

        if (currentTime !== this.getCurrentState().timeElapsed) {
            this.setTime(currentTime)
        }

        const currentSong =
            this.getCurrentState().songQueue.currentSong?.song ?? null
        if (currentSong === null) {
            this.endedSongID = null
        } else if (
            shouldAdvanceFromCurrentTrack(currentTime, currentSong) &&
            this.endedSongID !== currentSong.id
        ) {
            this.endedSongID = currentSong.id
            try {
                await this.onTrackEnded()
            } catch (error) {
                console.error("Failed to advance to the next track", error)
            }
        } else if (
            this.endedSongID === currentSong.id &&
            currentTime + 0.05 < currentSong.duration
        ) {
            this.endedSongID = null
        }

        // Continue the loop and save the id, so we can cancel it later
        if (!this.destroyed) {
            this.animationFrameID = requestAnimationFrame(
                this.animationFrameLoop.bind(this)
            )
        }
    }

    private async handlePlaybackActions(
        action: PlaybackActionType,
        seekTime: number,
        songID: string | null,
        actionVersion: number
    ) {
        const currentSong = this.getCurrentState().songQueue.currentSong
        if (!action || currentSong === null || currentSong.song.id !== songID) {
            this.resetPlaybackState(actionVersion)
            return
        }

        if (action === PlaybackActionType.PAUSE) {
            await this.pause(actionVersion)
            return
        }

        const lastBufferTime = this.getLastUninterruptedBufferTime()

        // Only proceed if the current song has buffers and the buffers meet the seek time
        if (lastBufferTime + 0.01 >= seekTime) {
            // Handle different player states here
            if (action === PlaybackActionType.PLAY) {
                await this.play(songID, seekTime, actionVersion)
            } else if (action === PlaybackActionType.SEEK) {
                await this.seek(songID, seekTime, actionVersion)
            }
        }
    }

    private async seek(
        songID: string,
        seekTime: number,
        actionVersion: number
    ) {
        const isPlaying = this.getCurrentState().status === "playing"

        try {
            if (this.mediaSourceSupport) {
                this.audioPlayer.currentTime = seekTime
                this.setTime(seekTime)
            } else {
                const decoded = this.getDecodedBuffer()
                if (decoded === null) {
                    return
                }

                const target = Math.max(0, Math.min(seekTime, decoded.duration))

                if (isPlaying && target < decoded.duration) {
                    await this.masterBus.resume()
                    if (!this.isActionCurrent(songID, actionVersion)) {
                        return
                    }
                    this.playAudioBuffer(decoded, target)
                } else {
                    this.stopWebAudioSource()
                    this.webAudioPausedPosition = target
                    this.webAudioOriginSeek = target
                    this.webAudioOriginContextTime = this.masterBus.currentTime
                    if (this.masterBus.state === "running") {
                        await this.masterBus.suspend()
                    }
                }

                this.setTime(target)
            }
        } catch (e) {
            console.error(e)
        }

        this.resetPlaybackState(actionVersion)
    }

    private resetPlaybackState(actionVersion?: number) {
        if (
            actionVersion === undefined ||
            actionVersion === this.playbackActionVersion
        ) {
            this.playbackState = null
        }
    }

    private isActionCurrent(songID: string, actionVersion: number): boolean {
        return (
            !this.destroyed &&
            actionVersion === this.playbackActionVersion &&
            this.getCurrentState().songQueue.currentSong?.song.id === songID
        )
    }

    /**
     * Buffers are being streamed from the server and may arrive out of order.
     * This method will find the end in seconds of the continuous set of buffers starting from the
     * beginning of the track. This value should be used to determine whether we're able to play the track
     * at a specific seek time.
     */
    private getLastUninterruptedBufferTime(): number {
        let uninterruptedBufferEnd = 0

        for (const buffer of this.preloadedBuffers.sort(
            (a, b) => a.start - b.start
        )) {
            const bufferEnd = this.getBufferEnd(buffer)
            if (buffer.start <= uninterruptedBufferEnd) {
                uninterruptedBufferEnd = Math.max(
                    uninterruptedBufferEnd,
                    bufferEnd
                )
            } else {
                break
            }
        }

        return Math.max(
            uninterruptedBufferEnd,
            this.getUninterruptedMediaBufferedEnd()
        )
    }

    private getBufferEnd(buffer: CurrentSongAudioBuffer): number {
        if (buffer.buffer instanceof AudioBuffer) {
            return Math.max(buffer.end, buffer.buffer.duration)
        }

        return buffer.end
    }

    private getPlaybackTime(): number {
        if (this.mediaSourceSupport) {
            return this.audioPlayer.currentTime || 0
        }

        if (this.webAudioPausedPosition != null) {
            return this.webAudioPausedPosition
        }

        if (!this.webAudioSourceStarted) {
            return this.webAudioOriginSeek
        }

        return (
            this.webAudioOriginSeek +
            Math.max(
                0,
                this.masterBus.currentTime - this.webAudioOriginContextTime
            )
        )
    }

    private getBufferedEnd(): number {
        if (
            !this.mediaSourceSupport ||
            this.audioPlayer.buffered.length === 0
        ) {
            return 0
        }

        return this.audioPlayer.buffered.end(
            this.audioPlayer.buffered.length - 1
        )
    }

    private getUninterruptedMediaBufferedEnd(): number {
        if (
            !this.mediaSourceSupport ||
            this.audioPlayer.buffered.length === 0
        ) {
            return 0
        }

        let uninterruptedEnd = 0
        for (let index = 0; index < this.audioPlayer.buffered.length; index++) {
            const start = this.audioPlayer.buffered.start(index)
            if (start > uninterruptedEnd + 0.01) {
                break
            }
            uninterruptedEnd = Math.max(
                uninterruptedEnd,
                this.audioPlayer.buffered.end(index)
            )
        }
        return uninterruptedEnd
    }

    /**
     *
     * Audio playback functions
     *
     */

    /**
     * Handles the playback of audio buffers using the mediasource or Web Audio API.
     * In case no buffers are available, it will attempt to load the first buffer. To be added!!!
     * @param id
     * @param seekTime
     */
    private async play(
        songID: string,
        seekTime: number,
        actionVersion: number
    ) {
        try {
            const lastBufferTime = this.getLastUninterruptedBufferTime()

            if (lastBufferTime + 0.01 < seekTime) {
                return
            }

            if (this.mediaSourceSupport) {
                this.audioPlayer.currentTime = seekTime

                await this.audioPlayer.play()
            } else {
                const decoded = this.getDecodedBuffer()
                if (decoded === null) {
                    return
                }

                if (seekTime >= decoded.duration) {
                    this.stopWebAudioSource()
                    this.webAudioPausedPosition = decoded.duration
                    this.setTime(decoded.duration)
                } else {
                    await this.masterBus.resume()

                    if (!this.isActionCurrent(songID, actionVersion)) {
                        return
                    }

                    this.playAudioBuffer(decoded, seekTime)
                }
            }
        } catch (e) {
            console.error(e)
            this.onPlaybackError(e)
        }

        this.resetPlaybackState(actionVersion)
    }

    private async pause(actionVersion?: number) {
        try {
            if (this.mediaSourceSupport) {
                this.audioPlayer.pause()
            } else {
                await this.pauseWebAudio()
            }
        } catch (e) {
            console.error(e)
        }

        this.resetPlaybackState(actionVersion)
    }

    private playAudioBuffer(audioBuffer: AudioBuffer, seekTime: number) {
        this.stopWebAudioSource()
        this.resetAudioContext()
        this.preloadAudioBuffer(audioBuffer)

        const safeSeekTime = Math.max(
            0,
            Math.min(seekTime, audioBuffer.duration)
        )
        this.currentDecodedBuffer = audioBuffer
        this.webAudioPausedPosition = null
        this.webAudioOriginSeek = safeSeekTime
        this.webAudioOriginContextTime = this.masterBus.currentTime

        if (this.soundSource && safeSeekTime < audioBuffer.duration) {
            const source = this.soundSource
            this.webAudioSourceStarted = true
            source.onended = () => {
                if (this.soundSource !== source) {
                    return
                }

                this.webAudioSourceStarted = false
                this.webAudioPausedPosition = audioBuffer.duration
                this.webAudioOriginSeek = audioBuffer.duration
            }
            source.start(0, safeSeekTime)
        } else {
            this.webAudioPausedPosition = audioBuffer.duration
        }
    }

    private resetAudioContext() {
        const volume = this.getCurrentState().volume
        try {
            this.audioContextGain?.disconnect()
        } catch {}
        this.audioContextGain = this.masterBus.createGain()
        this.audioContextGain.gain.setValueAtTime(volume, 0)
        this.audioContextGain.connect(this.masterBus.destination)
    }

    private preloadAudioBuffer(audioBuffer: AudioBuffer) {
        this.soundSource = this.masterBus.createBufferSource()
        this.soundSource.buffer = audioBuffer
        this.soundSource.connect(this.audioContextGain)
    }

    private async pauseWebAudio() {
        this.webAudioPausedPosition = this.getPlaybackTime()

        try {
            // Disconnect audioContextGain if conditions are met
            if (this.masterBus.state === "running") {
                try {
                    this.audioContextGain?.disconnect(
                        this.masterBus.destination
                    )
                } catch (error) {}
            }

            // Stop the sound source and clear the onended handler
            this.stopWebAudioSource()

            // Suspend the AudioContext to pause the masterBus
            await this.masterBus.suspend()
        } catch (error) {
            console.error("Error pausing web audio:", error)
        }

        this.audioContextGain = null
    }

    private stopWebAudioSource() {
        const source = this.soundSource
        this.soundSource = null
        this.webAudioSourceStarted = false

        if (!source) {
            return
        }

        source.onended = null
        try {
            source.stop()
        } catch {}
        try {
            source.disconnect()
        } catch {}
    }

    private getDecodedBuffer(): AudioBuffer | null {
        if (this.currentDecodedBuffer !== null) {
            return this.currentDecodedBuffer
        }

        const decoded = this.preloadedBuffers.find(
            buffer => buffer.buffer instanceof AudioBuffer
        )?.buffer
        return decoded instanceof AudioBuffer ? decoded : null
    }

    private async appendToSourceBuffer(
        buffer: CurrentSongAudioBuffer,
        retries: number = 100
    ): Promise<any> {
        try {
            if (this.destroyed) {
                return
            }

            if (!this.mediaSource || !this.sourceBuffer) {
                await this.initializeMediaSource()
            }

            if (this.destroyed || !this.sourceBuffer) {
                return
            }

            await this.waitForSourceBufferUpdate()

            this.sourceBuffer.appendBuffer(buffer.buffer as ArrayBuffer)

            await this.waitForSourceBufferUpdate()
        } catch (e) {
            if (this.destroyed) {
                return
            }

            if (retries <= 0) {
                console.error("Failed to append buffer to source buffer", e)
                return
            }

            await Misc.wait(0.1)

            return this.appendToSourceBuffer(buffer, retries - 1)
        }
    }

    private supportsMediaSourceType(): boolean {
        return (
            this.mediaSource != null &&
            typeof MediaSource !== "undefined" &&
            MediaSource.isTypeSupported(this.mimeType)
        )
    }

    private createMediaSource(): MediaSource | null {
        if (this.isIOS() || typeof MediaSource === "undefined") {
            return null
        }

        return new MediaSource()
    }

    private isIOS() {
        const iOS_1to12 = /iPad|iPhone|iPod/.test(navigator.platform)

        const isIOS = !window["MSStream"] && iOS_1to12

        return isIOS
    }

    private async waitForSourceBufferUpdate(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.sourceBuffer) {
                return resolve()
            }

            if (this.sourceBuffer.updating) {
                const cleanup = () => {
                    this.sourceBuffer?.removeEventListener(
                        "updateend",
                        updateendHandler
                    )
                    this.sourceBuffer?.removeEventListener(
                        "error",
                        errorHandler
                    )
                }
                const updateendHandler = () => {
                    cleanup()
                    resolve()
                }

                const errorHandler = (e: Event) => {
                    cleanup()
                    reject(e)
                }

                this.sourceBuffer.addEventListener(
                    "updateend",
                    updateendHandler
                )
                this.sourceBuffer.addEventListener("error", errorHandler)
            } else {
                resolve()
            }
        })
    }
}
